/**
 * Explosion VFX (Asset Pipeline B — 2026-06-05).
 *
 * Wires real CC0 explosion spritesheets into enemy + boss death feedback,
 * replacing the hand-rolled circle/rectangle particle bursts as the headline
 * effect (the bursts still ride along underneath for extra debris).
 *
 * Sheets (git-ignored, fetched by scripts/fetch-assets.sh section K):
 *   - pixel-explosion-12f.png  1152×96  → 12 frames @ 96×96 (1 row)
 *       小兵死亡用。Cuzco, CC-BY 3.0 (attribution in CREDITS.md).
 *   - ring-explosion.png       1000×600 → 100×100 frames, 10×6 grid (56 used)
 *       魔王死亡用。BenHickling, CC0.
 *
 * Every Phaser call is try-wrapped + texture-existence-gated so:
 *   - a fresh checkout (assets not yet fetched) silently no-ops, and
 *   - the headless test harness (no real loader / anim manager) never throws.
 *
 * Faction tint: callers pass a 24-bit color (門派 burst color) so all 5
 * factions read with a consistent palette instead of the raw orange sheet.
 */
import Phaser from 'phaser';

export interface ExplosionSheetDef {
  key: string;
  path: string;
  frameWidth: number;
  frameHeight: number;
  /** Last frame index used by the animation (sheet may pad with blanks). */
  endFrame: number;
  /** Animation frame rate (fps). */
  frameRate: number;
}

/** Small pixel explosion — enemy / minion deaths. */
export const PIXEL_EXPLOSION: ExplosionSheetDef = {
  key: 'vfx-pixel-explosion',
  path: 'assets/vfx/pixel-explosion-12f.png',
  frameWidth: 96,
  frameHeight: 96,
  endFrame: 11, // 12 frames, 0-indexed
  frameRate: 28,
};

/** Large ring explosion — boss deaths. */
export const RING_EXPLOSION: ExplosionSheetDef = {
  key: 'vfx-ring-explosion',
  path: 'assets/vfx/ring-explosion.png',
  frameWidth: 100,
  frameHeight: 100,
  endFrame: 55, // 56 used frames in the 10×6 = 60-cell grid
  frameRate: 40,
};

export const EXPLOSION_SHEETS: ExplosionSheetDef[] = [PIXEL_EXPLOSION, RING_EXPLOSION];

/** Animation key suffix (sheet.key + ANIM_SUFFIX). */
const ANIM_SUFFIX = '-anim';

/**
 * Queue every explosion spritesheet on the scene loader. Call from preload().
 * Mirrors the scene's existing load.image pattern: a missing file emits a
 * loaderror (swallowed by the scene's loader handler) and the texture.exists
 * gate in playExplosion keeps the call a no-op.
 */
export function preloadExplosionSheets(scene: Phaser.Scene): void {
  for (const sheet of EXPLOSION_SHEETS) {
    try {
      scene.load.spritesheet(sheet.key, sheet.path, {
        frameWidth: sheet.frameWidth,
        frameHeight: sheet.frameHeight,
      });
    } catch {
      /* headless harness lacks load.spritesheet */
    }
  }
}

/**
 * Create the play-once animation for each loaded sheet. Idempotent — skips
 * sheets whose texture failed to load and anims that already exist. Call from
 * create() after the loader has finished (Phaser populates the texture cache
 * before create()).
 *
 * Returns the list of anim keys actually created (test surface).
 */
export function ensureExplosionAnims(scene: Phaser.Scene): string[] {
  const created: string[] = [];
  for (const sheet of EXPLOSION_SHEETS) {
    const animKey = sheet.key + ANIM_SUFFIX;
    try {
      if (scene.anims?.exists?.(animKey)) continue;
      if (!scene.textures?.exists?.(sheet.key)) continue;
      scene.anims.create({
        key: animKey,
        frames: scene.anims.generateFrameNumbers(sheet.key, { start: 0, end: sheet.endFrame }),
        frameRate: sheet.frameRate,
        repeat: 0,
      });
      created.push(animKey);
    } catch {
      /* headless harness lacks anims manager */
    }
  }
  return created;
}

export interface PlayExplosionOpts {
  /** Sprite display scale (1 = native frame size). */
  scale?: number;
  /** 24-bit tint color (門派 burst color). Omit for the sheet's native palette. */
  tint?: number;
  /** Render depth. */
  depth?: number;
  /** Additive blend for a brighter, more energetic boom. Default true. */
  additive?: boolean;
}

/**
 * Play a one-shot explosion at (x, y) with the given sheet, then auto-destroy.
 *
 * Texture-gated + fully try-wrapped: returns false (no-op) when the sheet
 * texture is absent (fresh checkout / stripped build) or the scene lacks the
 * sprite/anims surface (headless tests). The caller's existing particle burst
 * + SFX still fire independently, so death feedback degrades gracefully.
 *
 * @returns true iff a sprite was spawned + the anim started.
 */
export function playExplosion(
  scene: Phaser.Scene,
  sheet: ExplosionSheetDef,
  x: number,
  y: number,
  opts: PlayExplosionOpts = {},
): boolean {
  if (!scene) return false;
  if (!scene.textures?.exists?.(sheet.key)) return false;
  const animKey = sheet.key + ANIM_SUFFIX;
  try {
    const sprite = scene.add.sprite(x, y, sheet.key);
    if (!sprite) return false;
    sprite.setDepth(opts.depth ?? 9);
    if (typeof opts.scale === 'number') sprite.setScale(opts.scale);
    if (typeof opts.tint === 'number') sprite.setTint(opts.tint);
    if (opts.additive !== false) {
      try { sprite.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
    }
    // Destroy when the play-once anim completes; guard for harnesses whose
    // sprite stub lacks .once / anim events.
    try {
      sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        try { sprite.destroy(); } catch { /* already torn down */ }
      });
    } catch { /* no event surface */ }
    sprite.play(animKey);
    return true;
  } catch {
    return false;
  }
}

/** Convenience: small pixel explosion for enemy / minion deaths. */
export function playEnemyExplosion(scene: Phaser.Scene, x: number, y: number, tint?: number): boolean {
  return playExplosion(scene, PIXEL_EXPLOSION, x, y, { scale: 0.9, tint, depth: 6 });
}

/** Convenience: large ring explosion for boss deaths. */
export function playBossExplosion(scene: Phaser.Scene, x: number, y: number, tint?: number): boolean {
  return playExplosion(scene, RING_EXPLOSION, x, y, { scale: 2.4, tint, depth: 9 });
}
